import { analyzeFrame, createWorkspace, isGoodQuad, warpQuad, enhance, canvasToBlob, resizeCanvas, borderInkSides, frameBorderBrightRatio } from "./docDetect.js";

// One invoice photo → ONE cropped, flattened, resized, metadata-free JPEG (canvas.toBlob never writes EXIF/GPS).
// Pure canvas maths, no network, no AI. Any doubt about the crop → the whole photo, resized, is used instead.
export const PIPELINE = "browser-v1";
const DEFAULTS = { maxSide: 2000, quality: 0.85, margin: 0.015, workSide: 2200, minBytes: 50 * 1024, borderInk: 0.1, minAreaFrac: 0.12, frameBright: 0.85 };

function polyArea(q) {
  let a = 0;
  for (let i = 0; i < 4; i++) { const [x1, y1] = q[i], [x2, y2] = q[(i + 1) % 4]; a += x1 * y2 - x2 * y1; }
  return Math.abs(a) / 2;
}

const pct = (v) => Math.round(v * 100);

// `full` is an upright canvas of the whole photo. Returns { canvas, cropped, reason, detail }.
export function cropAndResize(full, opts = {}, fallbackQuad = null) {
  const o = { ...DEFAULTS, ...opts };
  const whole = (reason, detail) => ({ canvas: resizeCanvas(full, o.maxSide), cropped: false, reason, detail });
  const bright = frameBorderBrightRatio(full);
  // screenshot / scan / PDF render: the page already fills the frame — an inner rectangle (a table) must never be mistaken for the page
  if (bright >= o.frameBright) return whole("frame_is_page", { frame_bright: pct(bright) });
  const r = analyzeFrame(full, createWorkspace(), 480);
  const detected = isGoodQuad(r) ? r.quad : null;
  if (detected && r.touchesBorder) return whole("touches_border", { frame_bright: pct(bright), area: pct(r.areaFrac) });
  const quad = detected || fallbackQuad;
  if (!quad) return whole("no_document", { frame_bright: pct(bright) });
  const area = polyArea(quad);
  if (area < o.minAreaFrac) return whole("crop_too_small", { frame_bright: pct(bright), area: pct(area) });
  try {
    const page = enhance(warpQuad(full, quad, o.maxSide, o.margin));
    // the band just inside the margin must be blank paper: ink there (dark pixels beyond the background that runs in from the
    // edge) means the crop cut through text → never upload that crop
    const ink = borderInkSides(page, 0.02, 2 * o.margin);
    const detail = { frame_bright: pct(bright), area: pct(area), ink: Object.fromEntries(Object.entries(ink.sides).map(([k, v]) => [k, pct(v)])) };
    if (ink.worst > o.borderInk) return { ...whole("text_at_edge", detail), rejected: page };
    return { canvas: page, cropped: true, reason: "ok", detail };
  } catch {
    return whole("warp_failed", { frame_bright: pct(bright), area: pct(area) });
  }
}

function meta(o, full, out, cropped, reason, detail, inputBytes, outputBytes, t0) {
  return { pipeline: PIPELINE, cropped, reason, detail, input_bytes: inputBytes, output_bytes: outputBytes, input_dims: [full.width, full.height],
    output_dims: [out.width, out.height], max_side: o.maxSide, quality: o.quality, duration_ms: Math.round(performance.now() - t0) };
}

// Camera capture path: `full` canvas → { blob, canvas, cropped, meta }.
export async function optimisePhotoCanvas(full, opts = {}, fallbackQuad = null) {
  const o = { ...DEFAULTS, ...opts };
  const t0 = performance.now();
  const { canvas, cropped, reason, detail } = cropAndResize(full, o, fallbackQuad);
  const blob = await canvasToBlob(canvas, o.quality);
  return { blob, canvas, cropped, meta: meta(o, full, canvas, cropped, reason, detail, null, blob.size, t0) };
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image(); // the browser applies EXIF orientation while decoding, so the canvas is upright
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.src = url;
  });
}

// File-picker path: photo File → { blob, cropped, meta } or null for non-images (PDFs pass through untouched).
export async function optimisePhotoFile(file, opts = {}) {
  const r = await optimisePhotoFileDebug(file, opts);
  return r && { blob: r.blob, cropped: r.cropped, meta: r.meta };
}

// Same as optimisePhotoFile but also returns the canvases (used by the Preview debug hook and tests).
export async function optimisePhotoFileDebug(file, opts = {}) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return null;
  const o = { ...DEFAULTS, ...opts };
  const t0 = performance.now();
  const img = await loadImage(file);
  const scale = Math.min(1, o.workSide / Math.max(img.naturalWidth, img.naturalHeight));
  const full = document.createElement("canvas");
  full.width = Math.round(img.naturalWidth * scale); full.height = Math.round(img.naturalHeight * scale);
  full.getContext("2d").drawImage(img, 0, 0, full.width, full.height);
  const small = file.size < o.minBytes;
  const { canvas, cropped, reason, detail, rejected } = small ? { canvas: full, cropped: false, reason: "small_passthrough", detail: null } : cropAndResize(full, o);
  let blob = await canvasToBlob(canvas, small ? 0.92 : o.quality);
  let finalReason = reason;
  if (!cropped && blob.size >= file.size) { blob = file; finalReason = "kept_source"; } // never upload a larger file than we were given
  return { blob, cropped, canvas, rejected, meta: meta(o, full, canvas, cropped, finalReason, detail, file.size, blob.size, t0) };
}
